import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Download, Printer, Filter, X, Mail, CalendarClock } from "lucide-react";
import { C, downloadCsv, chartsToCsv, openPrintReport } from "./reportsUi";
import {
  OverviewTab, WebsiteTab, LeadsTab, FormsChatTab, ConsultationsTab, ServiceLanesTab,
  TerritoriesTab, PartnersTab, FollowUpsTab, RevenueTab, FranchiseTab, SeoTab,
} from "./centerTabs";
import MoneyFlowTab from "./MoneyFlowTab";
import BuilderTab from "./BuilderTab";
import EmailReportDialog from "./EmailReportDialog";
import SchedulesDialog from "./SchedulesDialog";
import SavedViewsBar from "./SavedViewsBar";
import { HelpButton } from "@/components/help/HelpButton";

const TABS = [
  ["overview", "Overview"], ["website", "Website"], ["leads", "Leads"], ["forms-chat", "Forms & Chat"],
  ["consultations", "Consultations"], ["service-lanes", "Service Lanes"], ["territories", "Territories"],
  ["partners", "Partners"], ["follow-ups", "Follow-ups"], ["revenue", "Revenue"], ["money-flow", "Money Flow"],
  ["franchise", "Franchise"], ["seo", "SEO"], ["builder", "Builder"],
];
const ENDPOINT = {
  overview: "overview", website: "website", leads: "leads", "forms-chat": "forms-chat",
  consultations: "consultations", "service-lanes": "service-lanes", territories: "territories",
  partners: "partners", "follow-ups": "follow-ups", revenue: "revenue", "money-flow": "money-flow",
  franchise: "franchise", seo: "seo",
};
const emptyFilters = { date_from: "", date_to: "", branch_id: "all", franchise_id: "all", territory_id: "all", service_lane: "all", lead_source: "all", assigned_user: "all" };

export default function ReportingCenter() {
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [searchParams] = useSearchParams();
  const initialTab = TABS.some(([k]) => k === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [builderState, setBuilderState] = useState({ report_type: "lead_source", chart_type: "table", columns: [], value_col: null });
  const [emailOpen, setEmailOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const saveViewRef = useRef(null);

  useEffect(() => {
    api.get("/reports/center/meta").then(({ data: d }) => setMeta(d)).catch(() => toast.error("Could not load filters."));
  }, []);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    if (tab === "builder") { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get(`/reports/center/${ENDPOINT[tab]}`, { params });
      setData(d);
    } catch (e) { toast.error(errMsg(e, "Could not load report.")); setData(null); }
    finally { setLoading(false); }
  }, [tab, params]);
  useEffect(() => { load(); }, [load]);

  const tabLabel = TABS.find(([k]) => k === tab)?.[1] || "Report";
  const scopeLabel = meta?.is_super ? "All territories (global)" : "Your scoped territories";

  const exportCsv = () => {
    if (!data) return;
    if (data.table) downloadCsv(`reports-${tab}.csv`, data.table);
    else if (data.charts) chartsToCsv(`reports-${tab}.csv`, data.charts);
    else if (data.by_status) downloadCsv(`reports-${tab}.csv`, data.by_status);
    else return toast.error("Nothing to export on this tab.");
    toast.success("CSV exported.");
  };

  const printReport = () => {
    if (!data) return;
    const sections = [];
    if (data.cards) sections.push({ title: "Summary", columns: [{ key: "metric", label: "Metric" }, { key: "value", label: "Value" }], rows: Object.entries(data.cards).map(([k, v]) => ({ metric: k.replace(/_/g, " "), value: v == null ? "—" : v })) });
    if (data.by_status) sections.push({ title: "By status", columns: [{ key: "name", label: "Status" }, { key: "value", label: "Value" }], rows: data.by_status.map((s) => ({ name: s.name, value: s.not_tracked ? "Not tracked yet" : s.value })) });
    if (data.table) sections.push({ title: tabLabel, columns: Object.keys(data.table[0] || { note: "note" }).map((k) => ({ key: k, label: k.replace(/_/g, " ") })), rows: data.table });
    if (data.charts) Object.entries(data.charts).forEach(([name, arr]) => { if (arr && arr.length) sections.push({ title: name.replace(/_/g, " "), columns: [{ key: "name", label: "Name" }, { key: "value", label: "Value" }], rows: arr }); });
    if (!sections.length) return toast.error("Nothing to print on this tab.");
    openPrintReport(`Reporting Center — ${tabLabel}`, scopeLabel, sections);
  };

  const showFilters = tab !== "seo";
  const handleLoadView = (v) => {
    setTab(v.tab || "overview");
    setFilters({ ...emptyFilters, ...(v.filters || {}) });
    if (v.tab === "builder" && v.builder) setBuilderState({ report_type: "lead_source", chart_type: "table", columns: [], value_col: null, ...v.builder });
    toast.success(`Loaded view: ${v.name}`);
  };
  useEffect(() => {
    const vid = searchParams.get("view");
    if (!vid) return;
    api.get("/reports/center/views").then(({ data }) => {
      const v = (data.views || []).find((x) => x.id === vid);
      if (v) handleLoadView(v);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const renderTab = () => {
    if (tab === "builder") return <BuilderTab params={params} state={builderState} onChange={setBuilderState} onEmail={() => setEmailOpen(true)} onSaveView={() => saveViewRef.current && saveViewRef.current()} />;
    if (loading || !data) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;
    switch (tab) {
      case "overview": return <OverviewTab data={data} />;
      case "website": return <WebsiteTab data={data} />;
      case "leads": return <LeadsTab data={data} />;
      case "forms-chat": return <FormsChatTab data={data} />;
      case "consultations": return <ConsultationsTab data={data} />;
      case "service-lanes": return <ServiceLanesTab data={data} />;
      case "territories": return <TerritoriesTab data={data} />;
      case "partners": return <PartnersTab data={data} />;
      case "follow-ups": return <FollowUpsTab data={data} />;
      case "revenue": return <RevenueTab data={data} />;
      case "money-flow": return <MoneyFlowTab data={data} />;
      case "franchise": return <FranchiseTab data={data} />;
      case "seo": return <SeoTab data={data} />;
      default: return null;
    }
  };

  const F = ({ label, k, options, allLabel = "All" }) => (
    <div>
      <Label className="text-[11px] mb-1 block" style={{ color: C.slate }}>{label}</Label>
      <Select value={filters[k]} onValueChange={(v) => setFilters((p) => ({ ...p, [k]: v }))}>
        <SelectTrigger className="h-9 w-[150px] text-[13px]" data-testid={`rc-filter-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-[300px]">
          <SelectItem value="all">{allLabel}</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto pb-20" data-testid="reporting-center-page">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: C.charcoal }}>
            <BarChart3 size={22} style={{ color: C.navy }} /> Reporting Center
          </h1>
          <p className="text-[13px] mt-0.5" style={{ color: C.slate }}>{scopeLabel} · Aggregate-first. No private client data, no fabricated numbers.</p>
        </div>
        {tab !== "builder" && (
          <div className="flex items-center gap-2 flex-wrap">
            <SavedViewsBar current={{ tab, filters, builder: builderState }} onLoad={handleLoadView} registerSaver={(fn) => { saveViewRef.current = fn; }} />
            <HelpButton route="/reports" testid="rc-help-button" />
            <Button data-testid="rc-email" size="sm" variant="outline" onClick={() => setEmailOpen(true)} disabled={loading} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Mail size={14} /> Email</Button>
            <Button data-testid="rc-schedules" size="sm" variant="outline" onClick={() => setSchedOpen(true)} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><CalendarClock size={14} /> Schedules</Button>
            <Button data-testid="rc-export-csv" size="sm" variant="outline" onClick={exportCsv} disabled={loading} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Download size={14} /> CSV</Button>
            <Button data-testid="rc-print" size="sm" variant="outline" onClick={printReport} disabled={loading} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Printer size={14} /> Print / PDF</Button>
          </div>
        )}
        {tab === "builder" && (
          <div className="flex items-center gap-2 flex-wrap">
            <SavedViewsBar current={{ tab, filters, builder: builderState }} onLoad={handleLoadView} registerSaver={(fn) => { saveViewRef.current = fn; }} />
            <HelpButton route="/reports" testid="rc-help-button-builder" />
            <Button data-testid="rc-schedules" size="sm" variant="outline" onClick={() => setSchedOpen(true)} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><CalendarClock size={14} /> Schedules</Button>
          </div>
        )}
      </div>

      {showFilters && meta && (
        <Card className="p-3 border-[#E6EDF2] mb-4" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold" style={{ color: `${C.navy}B3` }}><Filter size={13} /> FILTERS</div>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-[11px] mb-1 block" style={{ color: C.slate }}>From</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))} className="h-9 w-[150px]" data-testid="rc-filter-from" /></div>
            <div><Label className="text-[11px] mb-1 block" style={{ color: C.slate }}>To</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))} className="h-9 w-[150px]" data-testid="rc-filter-to" /></div>
            {meta.branches?.length > 1 && <F label="Branch" k="branch_id" options={meta.branches.map((b) => ({ value: b.id, label: b.name }))} allLabel="All branches" />}
            {meta.territories?.length > 0 && <F label="Territory" k="territory_id" options={meta.territories.map((t) => ({ value: t.id, label: t.name }))} allLabel="All territories" />}
            {meta.franchises?.length > 0 && <F label="Franchise" k="franchise_id" options={meta.franchises.map((f) => ({ value: f.id, label: f.name }))} allLabel="All franchises" />}
            {meta.service_lanes?.length > 0 && <F label="Service lane" k="service_lane" options={meta.service_lanes.map((l) => ({ value: l.value, label: l.label }))} allLabel="All lanes" />}
            {meta.lead_sources?.length > 0 && <F label="Lead source" k="lead_source" options={meta.lead_sources.map((s) => ({ value: s, label: s }))} allLabel="All sources" />}
            {meta.users?.length > 0 && <F label="Assigned user" k="assigned_user" options={meta.users.map((u) => ({ value: u.id, label: u.name }))} allLabel="Anyone" />}
            <Button variant="ghost" size="sm" onClick={() => setFilters(emptyFilters)} className="h-9 gap-1" data-testid="rc-filter-clear"><X size={13} /> Clear</Button>
          </div>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 justify-start mb-4">
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} data-testid={`rc-tab-${k}`}
              className="text-[13px] px-3 py-1.5 rounded-full border data-[state=active]:bg-[#1C4358] data-[state=active]:text-white data-[state=active]:border-[#1C4358]"
              style={{ borderColor: C.line, color: C.navy }}>{label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={tab} className="mt-0">{renderTab()}</TabsContent>
      </Tabs>

      <EmailReportDialog open={emailOpen} onOpenChange={setEmailOpen} category={tab} filters={filters} builder={builderState} tabLabel={tabLabel} />
      <SchedulesDialog open={schedOpen} onOpenChange={setSchedOpen} category={tab} filters={filters} builder={builderState} tabLabel={tabLabel} />
    </div>
  );
}
